import { ProcessService } from './../../services/process.service';
import { Observable, Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ProcessAppraiseeApproval } from '../../models/process-models/process-appraisee-approval.model';
import {
  ProcessParticipationApprovalRequirementType,
  KnownProcessType,
} from '../../models/process-models';
import { MyTeamComponent } from '../my-team/my-team.component';
import { MyBuComponent } from '../my-bu/my-bu.component';
import { ConfirmationRespondentsDetailComponent } from './confirmation-respondents-detail/confirmation-respondents-detail.component';
import { RouterPathHelper } from '../../helpers/router-path-helper';
import { Except360ConfirmationRespondentsDetailComponent } from './except360-confirmation-respondents-detail/except360-confirmation-respondents-detail.component';
import { ConfirmationRespondentService } from '../../services/confirmation-respondent.service';
import { EnumDescription } from '../../helpers/enum-description.helper';
import { LanguageService } from '../../services/language.service';

@Component({
  selector: 'app-confirmation-respondents-list',
  templateUrl: './confirmation-respondents-list.component.html',
  styleUrls: ['./confirmation-respondents-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationRespondentsListComponent implements OnInit, OnDestroy {
  processAppraiseeApproval$: Observable<Array<ProcessAppraiseeApproval>>;
  requirementType: number;
  viewRequirementType: any = ProcessParticipationApprovalRequirementType;
  employeeId: string;
  businessUnitId: string;
  isMyTeamSurveyTab: boolean;
  isMyPageEstimateTab: boolean;
  isMyBUSurveyTab: boolean;

  private routePathHelper: RouterPathHelper;
  private surveyTabSubscription: Subscription = new Subscription();
  private readonly confirmationRespondentsDetailComponent =
    'confirmations-detail';
  private readonly except360ConfirmationRespondentsDetailComponent =
    'confirmations-bu-detail';
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private readonly processService: ProcessService,
    private readonly confirmationService: ConfirmationRespondentService,
    private readonly languageService: LanguageService,
    private readonly ref: ChangeDetectorRef
  ) {
    this.routePathHelper = new RouterPathHelper(this.route);
  }

  ngOnInit() {
    this.tabNavigationSubscriber();

    this.route.parent.paramMap.subscribe((x) => {
      this.resetTabState();
      this.requirementType = +x.get('requirementType');
      this.employeeId = x.get('teammateEmployeeId');
      this.isMyPageEstimateTab = x.get('isMyEstimateTabActive') === 'true';
      this.isMyBUSurveyTab = x.get('isMyBuSurveyTab') === 'true';
      this.isMyTeamSurveyTab = x.get('isMyTeamSurveyTab') === 'true';
      this.businessUnitId = x.get('businessUnitId');
      if (!this.requirementType) {
        const isBU = this.router.url.includes('my-bu');
        this.requirementType = isBU
          ? ProcessParticipationApprovalRequirementType.ByDepartmentAdministrator
          : ProcessParticipationApprovalRequirementType.ByAppraisee;
      }
      this.processAppraiseeApproval$ = this.getAllApproval(
        this.requirementType,
        this.employeeId,
        this.businessUnitId
      );
      this.ref.markForCheck();
    });
    this.changeLanguage();
  }

  ngOnDestroy() {
    if (this.surveyTabSubscription) {
      this.surveyTabSubscription.unsubscribe();
    }
  }

  getIsApprovalTab(): boolean {
    return (
      !this.isMyPageEstimateTab &&
      !this.isMyTeamSurveyTab &&
      !this.isMyBUSurveyTab
    );
  }

  getProcessAppraiseeApprovals(
    processAppraiseeApprovals: Array<ProcessAppraiseeApproval>
  ): Array<ProcessAppraiseeApproval> {
    if (this.isMyPageEstimateTab) {
      return processAppraiseeApprovals.filter((x) => x.allowSelfAssesment);
    }
    return processAppraiseeApprovals;
  }

  navigateToConfirm(processAppraiseeApproval: ProcessAppraiseeApproval) {
    this.getNavigationRoute(processAppraiseeApproval);
  }

  // TODO: survey report routing
  navigateToReport(processAppraiseeApproval: ProcessAppraiseeApproval) {}

  // TODO: survey report routing
  navigateToReportFromBu(processAppraiseeApproval: ProcessAppraiseeApproval) {}

  getProcessTypes(processKnownTypes: Array<number>): Array<string> {
    return processKnownTypes.map((x) =>
      EnumDescription.GetDescription(KnownProcessType, x)
    );
  }

  private resetTabState() {
    this.isMyBUSurveyTab = false;
    this.isMyPageEstimateTab = false;
    this.isMyTeamSurveyTab = false;
  }

  private getNavigationRoute(
    processAppraiseeApproval: ProcessAppraiseeApproval
  ) {
    const _360Index = processAppraiseeApproval.processKnownTypes.findIndex(
      (x) => x === KnownProcessType._360
    );
    const component =
      _360Index !== -1
        ? this.confirmationRespondentsDetailComponent
        : this.except360ConfirmationRespondentsDetailComponent;
    const path = this.routePathHelper.getNavigationPath(
      processAppraiseeApproval.processDurationAppraiseeId,
      component
    );
    this.router.navigate([path, { requirementType: this.requirementType }]);
  }

  private tabNavigationSubscriber() {
    this.surveyTabSubscription =
      this.confirmationService.myTeamSurveyTabClicked$.subscribe((x) => {
        this.isMyTeamSurveyTab = x;
        this.ref.detectChanges();
      });
    const subscriptionMyBuPage =
      this.confirmationService.myBuSurveyTabClicked$.subscribe((x) => {
        this.isMyBUSurveyTab = x;
        this.ref.detectChanges();
      });
    this.surveyTabSubscription.add(subscriptionMyBuPage);
  }

  private getAllApproval(
    requirementType: ProcessParticipationApprovalRequirementType,
    employeeId: string,
    businessUnitId: string
  ): Observable<Array<ProcessAppraiseeApproval>> {
    if (employeeId) {
      return this.processService.getApprovalByPM(employeeId, requirementType);
    }
    if (businessUnitId) {
      return this.processService.getApprovalByBU(
        businessUnitId,
        requirementType
      );
    }
    if (this.isMyPageEstimateTab) {
      return this.processService.getApprovalSelfAssesment(requirementType);
    }
    return this.processService.getAllApproval(this.requirementType);
  }

  private changeLanguage() {
    this.languageService.languageChanged$.subscribe((selectedLanguage) => {
      this.processAppraiseeApproval$ = this.getAllApproval(
        this.requirementType,
        this.employeeId,
        this.businessUnitId
      );
    });
  }
}
